/**
 * VirtualScroller Component
 * Timeline component that renders all thumbnails (no virtualization)
 *
 * Features:
 * - Renders all thumbnails for smooth scrolling
 * - Uses absolute positioning for layout
 * - Auto-center current photo during slider use
 * - React.memo optimized ThumbnailItem for performance
 *
 * Phase 2: Gallery PLAYER mode
 */

import React, { useEffect, useRef, useCallback } from "react";
import { Photo } from "@/types";
import { AnimatedCheckbox } from "@/components/AnimatedCheckbox";

// Constants
const THUMB_WIDTH = 100;
const THUMB_HEIGHT = 100;
const GAP = 8;
const ITEM_WIDTH = THUMB_WIDTH + GAP;

/**
 * Memoized thumbnail item component for performance
 * Prevents re-renders of all thumbnails when only currentIndex changes
 */
interface ThumbnailItemProps {
  photo: Photo;
  index: number;
  isSelected: boolean;
  isCurrent: boolean;
  imagesPreloaded: boolean;
  hasAnySelection: boolean;
  onSelect: (photoId: string, index: number, shiftKey: boolean) => void;
  onClick: (photo: Photo, index: number) => void;
}

const ThumbnailItem = React.memo<ThumbnailItemProps>(
  ({ photo, index, isSelected, isCurrent, imagesPreloaded, hasAnySelection, onSelect, onClick }) => (
    <div
      key={photo.photoId}
      className="cursor-pointer group"
      style={{
        position: "absolute",
        left: index * ITEM_WIDTH,
        width: THUMB_WIDTH,
        height: THUMB_HEIGHT,
      }}
      onClick={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest(".checkbox-overlay")) {
          onSelect(photo.photoId, index, e.shiftKey);
        } else {
          onClick(photo, index);
        }
      }}
    >
      {/* Thumbnail image */}
      <img
        src={photo.urls?.thumb200}
        alt=""
        className={`
          w-full h-full object-cover rounded no-select-drag
          transition-all duration-150
          ${isSelected ? "ring-2 ring-blue-300" : "hover:ring-2 hover:ring-gray-300"}
        `}
        style={{ background: "#1a1a1a" }}
        loading={imagesPreloaded ? "eager" : "lazy"}
        draggable={false}
        onError={(e) => {
          const img = e.target as HTMLImageElement;
          img.style.visibility = "hidden";
          console.warn(`[VirtualScroller] Failed to load thumbnail: ${photo.photoId}`);
        }}
      />

      {/* Selection checkbox overlay */}
      <div className={`checkbox-overlay absolute top-1 left-1 z-10 transition-opacity duration-150 ${isSelected || hasAnySelection ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
        <AnimatedCheckbox
          checked={isSelected}
          onChange={(e) => onSelect(photo.photoId, index, e.shiftKey)}
          size={18}
          id={photo.photoId}
        />
      </div>

      {/* Current photo indicator */}
      {isCurrent && (
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-blue-500 rounded-b" />
      )}
    </div>
  ),
  // Custom comparison: only re-render if these props change
  (prevProps, nextProps) =>
    prevProps.photo.photoId === nextProps.photo.photoId &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isCurrent === nextProps.isCurrent &&
    prevProps.imagesPreloaded === nextProps.imagesPreloaded &&
    prevProps.hasAnySelection === nextProps.hasAnySelection
);
ThumbnailItem.displayName = "ThumbnailItem";

interface VirtualScrollerProps {
  photos: Photo[];
  visibleCount?: number; // Unused - kept for API compatibility
  currentIndex: number;
  selectedIds: Set<string>;
  onPhotoClick: (photo: Photo, index: number) => void;
  onSelectionToggle: (photoId: string, index: number, shiftKey: boolean) => void;
  autoCenter?: boolean; // Whether to auto-center current photo (true during slider use)
  velocity?: number; // Current playback velocity
  imagesPreloaded?: boolean; // If true, skip loading="lazy" since images are already cached
}

export function VirtualScroller({
  photos,
  visibleCount: _visibleCountProp, // Unused - kept for API compatibility
  currentIndex,
  selectedIds,
  onPhotoClick,
  onSelectionToggle,
  autoCenter = false,
  velocity = 0,
  imagesPreloaded = false,
}: VirtualScrollerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lastDisplayedIndexRef = useRef(currentIndex);

  // Always sync scroll position with currentIndex (controlled by external slider)
  // At high velocity, throttle updates for performance
  useEffect(() => {
    if (!containerRef.current) return;

    const absVelocity = Math.abs(velocity);

    // Calculate stride for timeline updates based on velocity (performance optimization)
    // At 2000fps, stride of 20 means ~100 visual updates/sec (still smooth but less work)
    let timelineStride = 1;
    if (absVelocity > 1000) {
      timelineStride = 20;
    } else if (absVelocity > 500) {
      timelineStride = 10;
    } else if (absVelocity > 100) {
      timelineStride = 5;
    }

    // Only throttle during high-velocity playback
    if (autoCenter && absVelocity > 0.1) {
      const indexDelta = Math.abs(currentIndex - lastDisplayedIndexRef.current);
      if (indexDelta < timelineStride) {
        return;
      }
    }
    lastDisplayedIndexRef.current = currentIndex;

    const container = containerRef.current;
    const targetScroll =
      currentIndex * ITEM_WIDTH - container.clientWidth / 2 + ITEM_WIDTH / 2;
    const clampedScroll = Math.max(
      0,
      Math.min(targetScroll, container.scrollWidth - container.clientWidth)
    );

    container.scrollLeft = clampedScroll;
  }, [currentIndex, velocity, autoCenter]);

  // Handle scroll - no throttling needed since we're not updating state
  const handleScroll = useCallback((_e: React.UIEvent<HTMLDivElement>) => {
    // No state update needed - all thumbnails are always rendered
  }, []);

  // Handle mouse wheel for horizontal scroll
  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!containerRef.current) return;

    // Prevent default vertical scroll
    e.preventDefault();

    // Convert vertical wheel to horizontal scroll (natural scrolling)
    containerRef.current.scrollLeft += e.deltaY;
  }, []);

  return (
    <div className="virtual-scroller w-full">
      <div
        ref={containerRef}
        className="overflow-x-scroll overflow-y-hidden"
        onScroll={handleScroll}
        onWheel={handleWheel}
        style={{
          height: "110px",
          scrollbarWidth: "none", // Firefox
          msOverflowStyle: "none", // IE/Edge
        }}
      >
        {/* Container with full width for scroll, uses relative positioning */}
        <div
          style={{
            width: `${photos.length * ITEM_WIDTH}px`,
            height: `${THUMB_HEIGHT}px`,
            position: "relative",
            marginTop: "4px", // Space for hover ring at top
          }}
        >
          {/* Render all photos with memoized ThumbnailItem for performance */}
          {photos.map((photo, index) => (
            <ThumbnailItem
              key={photo.photoId}
              photo={photo}
              index={index}
              isSelected={selectedIds.has(photo.photoId)}
              isCurrent={index === currentIndex}
              imagesPreloaded={imagesPreloaded}
              hasAnySelection={selectedIds.size > 0}
              onSelect={onSelectionToggle}
              onClick={onPhotoClick}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
